import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Chip,
  Paper,
  Avatar,
  Fade,
  Grow,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Spa,
  LocalFlorist,
  EmojiNature,
  ArrowForward,
  VerifiedUser,
  Nature,
  AutoAwesome,
} from '@mui/icons-material';

const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    setChecked(true);
  }, []);

  return (
    <Box
      id="home"
      className="home_custom"
      sx={{
        minHeight: { xs: 'auto', md: '85vh' },
        display: 'flex',
        alignItems: 'flex-start',
        background: `
          radial-gradient(circle at 20% 30%, rgba(201, 169, 97, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(201, 169, 97, 0.06) 0%, transparent 50%),
          linear-gradient(135deg, #FFFFFF 0%, #F8F6F1 30%, #E8E3D8 60%, #F5F2EB 100%)
        `,
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 8, md: 9 },
        pb: { xs: 6, md: 8 },
        width: '100%',
      }}
    >
      {/* Premium Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            repeating-linear-gradient(45deg, transparent, transparent 50px, rgba(201, 169, 97, 0.02) 50px, rgba(201, 169, 97, 0.02) 51px),
            repeating-linear-gradient(-45deg, transparent, transparent 50px, rgba(201, 169, 97, 0.02) 50px, rgba(201, 169, 97, 0.02) 51px)
          `,
          opacity: 0.5,
        }}
      />

      {/* Floating Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: { xs: '200px', md: '350px' },
          height: { xs: '200px', md: '350px' },
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(201, 169, 97, 0.15) 0%, rgba(201, 169, 97, 0.05) 50%, transparent 70%)`,
          filter: 'blur(40px)',
          animation: 'float 25s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
            '33%': { transform: 'translate(30px, -30px) scale(1.1)' },
            '66%': { transform: 'translate(-20px, 20px) scale(0.9)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: { xs: '180px', md: '300px' },
          height: { xs: '180px', md: '300px' },
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(201, 169, 97, 0.12) 0%, rgba(201, 169, 97, 0.04) 50%, transparent 70%)`,
          filter: 'blur(35px)',
          animation: 'float 20s ease-in-out infinite reverse',
        }}
      />

      {/* Left Side Wellness Features */}
      <Fade in={checked} timeout={2000}>
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '-200px', md: '20px', lg: '60px' },
            top: '50%',
            transform: 'translateY(-50%)',
            display: { xs: 'none', lg: 'flex' },
            flexDirection: 'column',
            gap: 4,
            maxWidth: '200px',
          }}
        >
          {/* Feature 1 */}
          <Box sx={{ textAlign: 'right' }}>
            <Stack spacing={1} alignItems="flex-end">
              <LocalFlorist sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Ancient Wisdom
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                5,000 years of Ayurvedic healing tradition
              </Typography>
            </Stack>
          </Box>

          {/* Feature 2 */}
          <Box sx={{ textAlign: 'right' }}>
            <Stack spacing={1} alignItems="flex-end">
              <Spa sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Holistic Care
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                Mind, body & spirit in perfect harmony
              </Typography>
            </Stack>
          </Box>

          {/* Feature 3 */}
          <Box sx={{ textAlign: 'right' }}>
            <Stack spacing={1} alignItems="flex-end">
              <EmojiNature sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Natural Healing
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                100% organic herbs & remedies
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Fade>

      {/* Right Side Wellness Benefits */}
      <Fade in={checked} timeout={2200}>
        <Box
          sx={{
            position: 'absolute',
            right: { xs: '-200px', md: '20px', lg: '60px' },
            top: '50%',
            transform: 'translateY(-50%)',
            display: { xs: 'none', lg: 'flex' },
            flexDirection: 'column',
            gap: 4,
            maxWidth: '200px',
          }}
        >
          {/* Benefit 1 */}
          <Box sx={{ textAlign: 'left' }}>
            <Stack spacing={1} alignItems="flex-start">
              <VerifiedUser sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Expert Guidance
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                Certified practitioners with years of experience
              </Typography>
            </Stack>
          </Box>

          {/* Benefit 2 */}
          <Box sx={{ textAlign: 'left' }}>
            <Stack spacing={1} alignItems="flex-start">
              <AutoAwesome sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Personalized Plans
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                Tailored wellness programs for your unique needs
              </Typography>
            </Stack>
          </Box>

          {/* Benefit 3 */}
          <Box sx={{ textAlign: 'left' }}>
            <Stack spacing={1} alignItems="flex-start">
              <Nature sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Sustainable Practice
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                }}
              >
                Eco-friendly & ethically sourced ingredients
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Fade>

      {/* Floating Sparkles */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '15%',
          fontSize: 24,
          color: 'primary.main',
          opacity: 0,
          animation: 'sparkle1 3s ease-in-out infinite',
          '@keyframes sparkle1': {
            '0%, 100%': { opacity: 0, transform: 'scale(0.5)' },
            '50%': { opacity: 0.8, transform: 'scale(1.2)' },
          },
        }}
      >
        ✦
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '70%',
          right: '18%',
          fontSize: 20,
          color: 'primary.main',
          opacity: 0,
          animation: 'sparkle2 4s ease-in-out infinite',
          animationDelay: '1s',
          '@keyframes sparkle2': {
            '0%, 100%': { opacity: 0, transform: 'scale(0.5)' },
            '50%': { opacity: 0.7, transform: 'scale(1.3)' },
          },
        }}
      >
        ✦
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '35%',
          right: '12%',
          fontSize: 22,
          color: 'primary.main',
          opacity: 0,
          animation: 'sparkle3 3.5s ease-in-out infinite',
          animationDelay: '2s',
          '@keyframes sparkle3': {
            '0%, 100%': { opacity: 0, transform: 'scale(0.5)' },
            '50%': { opacity: 0.6, transform: 'scale(1.1)' },
          },
        }}
      >
        ✦
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '55%',
          left: '20%',
          fontSize: 18,
          color: 'primary.main',
          opacity: 0,
          animation: 'sparkle4 4.5s ease-in-out infinite',
          animationDelay: '1.5s',
          '@keyframes sparkle4': {
            '0%, 100%': { opacity: 0, transform: 'scale(0.5)' },
            '50%': { opacity: 0.75, transform: 'scale(1.4)' },
          },
        }}
      >
        ✦
      </Box>

      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ width: '100%', maxWidth: '100%' }}>
          {/* Premium Logo Section */}
          <Fade in={checked} timeout={1200}>
            <Box sx={{ position: 'relative', mb: 1 }}>
              {/* Outer Glow Ring */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 240, md: 320 },
                  height: { xs: 240, md: 320 },
                  borderRadius: '50%',
                  background: `radial-gradient(circle, rgba(201, 169, 97, 0.1) 0%, transparent 70%)`,
                  filter: 'blur(20px)',
                }}
              />

              {/* Rotating Ring */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 220, md: 290 },
                  height: { xs: 220, md: 290 },
                  borderRadius: '50%',
                  border: `2px solid ${theme.palette.primary.main}30`,
                  animation: 'rotate 40s linear infinite',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    boxShadow: `0 0 20px ${theme.palette.primary.main}`,
                  },
                  '@keyframes rotate': {
                    from: { transform: 'translate(-50%, -50%) rotate(0deg)' },
                    to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
                  },
                }}
              />

              {/* Main Logo Circle */}
              <Paper
                elevation={8}
                sx={{
                  width: { xs: 180, md: 240 },
                  height: { xs: 180, md: 240 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#FFFFFF',
                  border: `4px solid ${theme.palette.primary.main}`,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: `
                    0 20px 60px rgba(201, 169, 97, 0.3),
                    inset 0 2px 20px rgba(201, 169, 97, 0.05),
                    0 0 0 8px rgba(201, 169, 97, 0.05)
                  `,
                  animation: 'pulse 4s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.02)' },
                  },
                }}
              >
                <Box
                  component="img"
                  className="logo-adjust"
                  src={`${process.env.PUBLIC_URL}/veda-vine-logo.png`}
                  alt="Veda & Vine Logo"
                  sx={{
                    width: '90%',
                    height: '90%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 12px rgba(201, 169, 97, 0.2))',
                  }}
                />
              </Paper>
            </Box>
          </Fade>

          {/* Premium Title Section */}
          <Stack spacing={2} alignItems="center">
            <Slide direction="down" in={checked} timeout={1000}>
              <Box sx={{ textAlign: 'center', position: 'relative' }}>
                {/* Decorative Top Line */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ width: { xs: 40, md: 60 }, height: 1, bgcolor: 'primary.main', opacity: 0.3 }} />
                  <LocalFlorist sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Box sx={{ width: { xs: 40, md: 60 }, height: 1, bgcolor: 'primary.main', opacity: 0.3 }} />
                </Box>

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.75rem', sm: '4rem', md: '5.5rem' },
                    color: 'text.primary',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    mb: 1.5,
                    background: `linear-gradient(135deg, #1A1A1A 0%, #4A4A4A 50%, #1A1A1A 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: 'none',
                    position: 'relative',
                    '&::after': {
                      content: '"Veda & Vine"',
                      position: 'absolute',
                      top: 2,
                      left: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}10 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      zIndex: -1,
                    },
                  }}
                >
                  Veda & Vine
                </Typography>

                {/* Decorative Bottom Line */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ width: { xs: 30, md: 50 }, height: 2, bgcolor: 'primary.main' }} />
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  <Box sx={{ width: { xs: 30, md: 50 }, height: 2, bgcolor: 'primary.main' }} />
                </Box>
              </Box>
            </Slide>

            <Fade in={checked} timeout={1400}>
              <Paper
                elevation={0}
                className='wellness-btn'
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 4,
                  py: 1.5,
                  borderRadius: 50,
                  border: `2px solid ${theme.palette.primary.dark}`,
                  boxShadow: `0 8px 24px rgba(201, 169, 97, 0.3)`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.25em',
                    fontSize: { xs: '0.7rem', md: '0.85rem' },
                  }}
                >
                  ✦ WELLNESS FROM WITHIN ✦
                </Typography>
              </Paper>
            </Fade>
          </Stack>

          {/* Premium Description */}
          <Fade in={checked} timeout={1600}>
            <Box sx={{ maxWidth: 650, px: { xs: 3, md: 0 } }}>
              <Typography
                variant="h5"
                sx={{
                  textAlign: 'center',
                  color: 'text.primary',
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                  lineHeight: 1.9,
                  fontWeight: 400,
                  mb: 1,
                }}
              >
                Discover the ancient wisdom of holistic healing combined with modern
                wellness practices.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.8,
                  fontStyle: 'italic',
                }}
              >
                Experience transformation through personalized care and natural remedies.
              </Typography>
            </Box>
          </Fade>

          {/* Premium CTA Buttons */}
          <Grow in={checked} timeout={1800}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' }, px: { xs: 3, sm: 0 } }}
            >
              <Button
                variant="contained"
                className='begin-btn'
                size="large"
                href="/shop"
                endIcon={<ArrowForward />}
                sx={{
                  py: 2,
                  px: 5,
                  borderRadius: 50,
                  textTransform: 'none',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 12px 32px rgba(201, 169, 97, 0.4)`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    boxShadow: `0 16px 40px rgba(201, 169, 97, 0.5)`,
                    transform: 'translateY(-3px)',
                    '&::before': {
                      left: '100%',
                    },
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Begin Your Journey
              </Button>
              <Button
                variant="contained"
                className='begin-btn'
                size="large"
                href='#services'
                endIcon={<ArrowForward />}
                sx={{
                  py: 2,
                  px: 5,
                  borderRadius: 50,
                  textTransform: 'none',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 12px 32px rgba(201, 169, 97, 0.4)`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    boxShadow: `0 16px 40px rgba(201, 169, 97, 0.5)`,
                    transform: 'translateY(-3px)',
                    '&::before': {
                      left: '100%',
                    },
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Explore Services
              </Button>

            </Stack>
          </Grow>

          {/* Premium Trust Indicators */}
          <Fade in={checked} timeout={2000}>
  <Paper
    elevation={4}
    className='highlight'
    sx={{
      mt: 3,
      px: { xs: 3, md: 6 },
      py: 4,
      width: '80%',
      maxWidth: '1300px',
      mx: 'auto',
      borderRadius: 4,
      bgcolor: 'rgba(255, 255, 255, 0.96)',
      backdropFilter: 'blur(10px)',
      border: `1px solid ${theme.palette.primary.main}20`,
      boxShadow: `0 16px 48px rgba(201, 169, 97, 0.15)`,
    }}
  >
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={4}
      alignItems="center"
      justifyContent="space-between"
      divider={
        !isMobile && (
          <Box
          className="w-auto"
            sx={{
              width: 1,
              bgcolor: 'primary.main',
              opacity: 0.2,
            }}
          />
        )
      }
    >
      {/* Shilajit */}
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <Box
          component="img"
          src="/Shilajit.png"
          alt="Shilajit"
          sx={{
            width: 90,
            height: 90,
            objectFit: 'cover',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: '0 6px 18px rgba(201,169,97,0.25)',
          }}
        />
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: 'primary.dark',
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            Shilajit
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pure Himalayan
          </Typography>
        </Box>
      </Stack>

      {/* Shatavari */}
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <Box
          component="img"
          src="/Shatavari.png"
          alt="Shatavari"
          sx={{
            width: 90,
            height: 90,
            objectFit: 'cover',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: '0 6px 18px rgba(201,169,97,0.25)',
          }}
        />
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: 'primary.dark',
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            Shatavari
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ayurvedic Herb
          </Typography>
        </Box>
      </Stack>

      {/* Gluten Free */}
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <Box
          component="img"
          src="/Gluten_Free.jpg"
          alt="Gluten Free"
          sx={{
            width: 90,
            height: 90,
            objectFit: 'cover',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: '0 6px 18px rgba(201,169,97,0.25)',
          }}
        />
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: 'primary.dark',
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            Gluten Free
          </Typography>
          <Typography variant="body2" color="text.secondary">
            100% Natural
          </Typography>
        </Box>
      </Stack>
    </Stack>
  </Paper>
</Fade>




          {/* Scroll Indicator */}
          {/* <Fade in={checked} timeout={2000}>
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
                transition: 'transform 0.3s ease',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                Scroll to Explore
              </Typography>
              <Box
                sx={{
                  width: 24,
                  height: 40,
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 20,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 4,
                    height: 10,
                    bgcolor: 'primary.main',
                    borderRadius: 2,
                    animation: 'scroll 2s ease-in-out infinite',
                  },
                  '@keyframes scroll': {
                    '0%': { transform: 'translate(-50%, 0)', opacity: 0 },
                    '50%': { opacity: 1 },
                    '100%': { transform: 'translate(-50%, 18px)', opacity: 0 },
                  },
                }}
              />
            </Box>
          </Fade> */}
        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection;
